import { useMemo, type FC } from "react";
import type { ErrorListProps } from "@rjsf/utils";
import { useTranslation } from "react-i18next";
import { FormHelperText, styled, Alert, AlertTitle } from "@mui/material";

const StyledAlert = styled(Alert)`
  padding-top: 16px;
`;

const StyledFormHelperText = styled(FormHelperText)(({}) => ({
  margin: 0,
}));

const StyledList = styled("ul")(({}) => ({
  margin: 0,
  padding: 0,
  paddingLeft: "1rem",
}));
const StyledListItem = styled("li")(({}) => ({
  margin: "1rem 0",
}));

function getFieldTitle(propertyKeys: string[], uiSchema: any, schema: any) {
  let currentUiSchema = uiSchema;
  let currentSchema = schema;
  for (const key of propertyKeys) {
    currentUiSchema = currentUiSchema?.[key] ?? {};
    currentSchema = currentSchema?.properties?.[key] ?? {};
  }
  return currentUiSchema["ui:title"] || currentSchema["title"] || propertyKeys.join(".");
}

function transformError(error: any, uiSchema: any, schema: any, t: any) {
  //Get all property keys excluding array indices in order to find the correct field title
  const propertyKeys = !!error.property ? error.property.split(".").filter((key: string) => isNaN(Number(key))) : [];

  const fieldTitle = getFieldTitle(propertyKeys, uiSchema, schema);
  const fallbackValue = t("kioForm.templates.ErrorListTemplate.errors.fallback.unknown");

  switch (error.name) {
    case "false schema":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.falseSchema");
      break;
    case "additionalItems":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.additionalItems", {
        count: error?.params?.limit ? parseInt(error.params.limit) : fallbackValue,
        fieldTitle: fieldTitle,
      });
      break;
    case "additionalProperties":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.additionalProperties");
      break;
    case "anyOf":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.anyOf", { fieldTitle: fieldTitle });
      break;
    case "dependencies":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.dependencies", {
        fieldTitle: fieldTitle,
        depProperty: error?.params?.property ?? fallbackValue,
      });
      break;
    case "enum":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.enum", {
        fieldTitle: fieldTitle,
        allowedValues: `: ${error?.params?.allowedValues?.join(", ") ?? fallbackValue}`,
      });
      break;
    case "format":
      const knownFormats = ["date-time", "date", "time", "email", "data-url", "uri"];
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.format", {
        fieldTitle: fieldTitle,
        format: knownFormats.includes(error?.params?.format)
          ? t(`kioForm.templates.ErrorListTemplate.errors.formats.${error.params.format}`)
          : error?.params?.format ?? "",
      });
      break;
    case "maximum":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.maximum", {
        fieldTitle: fieldTitle,
        max: error?.params?.limit ?? fallbackValue,
      });
      break;
    case "minimum":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.minimum", {
        fieldTitle: fieldTitle,
        min: error?.params?.limit ?? fallbackValue,
      });
      break;
    case "maxItems":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.maxItems", {
        fieldTitle: fieldTitle,
        count: error?.params?.limit ? parseInt(error.params.limit) : fallbackValue,
      });
      break;
    case "minItems":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.minItems", {
        fieldTitle: fieldTitle,
        count: error?.params?.limit ? parseInt(error.params.limit) : fallbackValue,
      });
      break;
    case "maxLength":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.maxLength", {
        fieldTitle: fieldTitle,
        count: error?.params?.limit ? parseInt(error.params.limit) : fallbackValue,
      });
      break;
    case "minLength":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.minLength", {
        fieldTitle: fieldTitle,
        count: error?.params?.limit ? parseInt(error.params.limit) : fallbackValue,
      });
      break;
    case "maxProperties":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.maxProperties", {
        fieldTitle: fieldTitle,
        count: error?.params?.limit ? parseInt(error.params.limit) : fallbackValue,
      });
      break;
    case "minProperties":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.minProperties", {
        fieldTitle: fieldTitle,
        count: error?.params?.limit ? parseInt(error.params.limit) : fallbackValue,
      });
      break;
    case "multipleOf":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.multipleOf", {
        fieldTitle: fieldTitle,
        multipleOf: error?.params?.multipleOf ?? fallbackValue,
      });
      break;
    case "not":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.not", { fieldTitle: fieldTitle });
      break;
    case "oneOf":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.oneOf", { fieldTitle: fieldTitle });
      break;
    case "pattern":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.pattern", {
        fieldTitle: fieldTitle,
        pattern: error?.params?.pattern ?? fallbackValue,
      });
      break;

    case "required":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.required", { fieldTitle: fieldTitle });
      break;
    case "type":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.type", {
        fieldTitle: fieldTitle,
        type: error?.params?.type ?? fallbackValue,
      });
      break;

    case "unevaluatedProperties":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.unevaluatedProperties");
      break;
    case "unevaluatedItems":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.unevaluatedItems", {
        fieldTitle: fieldTitle,
        count: error?.params?.len ?? fallbackValue,
      });
      break;

    case "uniqueItems":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.uniqueItems", { fieldTitle: fieldTitle });
      break;
    case "propertyNames":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.propertyNames", {
        propertyName: error?.params?.propertyName ?? fallbackValue,
      });
      break;
    case "const":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.const", {
        fieldTitle: fieldTitle,
        allowedValue: error?.params?.allowedValue ?? fallbackValue,
      });
      break;
    case "contains":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.contains", {
        fieldTitle: fieldTitle,
        count: error?.params?.minContains ? parseInt(error.params.minContains) : fallbackValue,
      });
      break;
    case "formatMaximum":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.formatMaximum", {
        fieldTitle: fieldTitle,
        max: error?.params?.limit ?? fallbackValue,
      });
      break;
    case "formatMinimum":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.formatMinimum", {
        fieldTitle: fieldTitle,
        min: error?.params?.limit ?? fallbackValue,
      });
      break;
    case "if":
      error.errorMessage = t("kioForm.templates.ErrorListTemplate.errors.if", { fieldTitle: fieldTitle });
      break;
    default:
      error.errorMessage = error.stack;
      break;
  }
  return error;
}

const ErrorListTemplate: FC<ErrorListProps> = ({ errors, uiSchema, schema }) => {
  const { t } = useTranslation("common");

  const transformedErrors = useMemo(() => {
    const unique = new Set<string>();
    return errors
      .map((error) => transformError(error, uiSchema, schema, t))
      .filter((error) => {
        if (unique.has(error.errorMessage)) return false;
        unique.add(error.errorMessage);
        return true;
      });
  }, [errors, uiSchema, schema, t]);

  return (
    <StyledAlert severity="error">
      <AlertTitle>{t("kioForm.templates.ErrorListTemplate.heading")}</AlertTitle>
      <StyledList>
        {transformedErrors.map((error: any) => (
          <StyledListItem key={error.stack}>
            <span>{error.errorMessage}</span>
            <StyledFormHelperText>
              {t("kioForm.templates.ErrorListTemplate.errors.errorName", { errorName: error.name })}
            </StyledFormHelperText>
          </StyledListItem>
        ))}
      </StyledList>
    </StyledAlert>
  );
};

export default { ErrorListTemplate };
